import {
  Body,
  Checkbox,
  type CheckboxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@e2n/ui/v2';
import { useTranslation } from 'react-i18next';
import type { AbgleichZeile } from '../../hooks/useMitarbeiterabgleich/entities/Mitarbeiterabgleich';
import { AbgleichZellen } from './AbgleichZellen';
import {
  ABGLEICH_SPALTEN,
  ABGLEICH_ZEILE_CLASS,
  type Quelle,
} from './abgleichKonstanten';

export type AbgleichAuswahl = {
  selectedIds: Set<string>;
  onToggleAll: (checked: boolean) => void;
  onToggleRow: (id: string, checked: boolean) => void;
};

export function AbgleichTabelle({
  titel,
  quelle,
  zeilen,
  auswahl,
  disabled,
}: {
  titel: string;
  quelle: Quelle;
  zeilen: AbgleichZeile[];
  auswahl?: AbgleichAuswahl;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const selected = (z: AbgleichZeile) => !!auswahl?.selectedIds.has(z.id);
  const allSelected = zeilen.length > 0 && zeilen.every(selected);
  const someSelected = zeilen.some(selected);
  let checkedState: CheckboxProps['checked'] = false;
  if (allSelected) {
    checkedState = true;
  } else if (someSelected) {
    checkedState = 'indeterminate';
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <Body weight="semibold">{titel}</Body>
      <Table>
        <TableHeader>
          <TableRow>
            {auswahl && (
              <TableHead className="w-8">
                <Checkbox
                  aria-label={t('domain.datev.abgleich.alleAuswaehlen')}
                  checked={checkedState}
                  disabled={disabled}
                  onCheckedChange={(checked) =>
                    auswahl.onToggleAll(checked === true)
                  }
                />
              </TableHead>
            )}
            {ABGLEICH_SPALTEN.map(({ feld, labelKey }) => (
              <TableHead key={feld}>{t(labelKey)}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {zeilen.map((zeile) => (
            <TableRow key={zeile.id}>
              {auswahl && (
                <TableCell type="checkbox" className={ABGLEICH_ZEILE_CLASS}>
                  <Checkbox
                    aria-label={t('domain.datev.abgleich.zeileAuswaehlen', {
                      id: zeile.id,
                    })}
                    checked={auswahl.selectedIds.has(zeile.id)}
                    disabled={disabled}
                    onCheckedChange={(checked) =>
                      auswahl.onToggleRow(zeile.id, checked === true)
                    }
                  />
                </TableCell>
              )}
              <AbgleichZellen zeile={zeile} quelle={quelle} />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
